import { useEffect, useRef, type ReactNode } from "react";
import { Stack } from "@fluentui/react";
import DOMPurify from "dompurify";
import { SectionPage } from "@snsw-gel/react";
import styles from "./Answer.module.css";
import type { TurnAgentStyle, TurnCitation, TurnFollowup } from "../chatTurn";
import SnswAvatar from "../../assets/snswAvatar";
import { Citations } from "../Citations";
import { FollowupQuestions } from "../FollowupQuestions";
import { OtherLinks } from "../OtherLinks";

interface Props {
  content: string;
  isStreaming: boolean;
  agentStyle?: TurnAgentStyle;
  /** Feedback UI is Bonds-only for now (its endpoint lives on the Bonds
   *  backend), so it arrives as a prebuilt slot rather than a contract the
   *  view-model has to know about. */
  feedbackSlot?: ReactNode;
  onCitationClicked: (filePath: string, citationText?: string) => void;
  citations?: TurnCitation[];
  otherLinks?: TurnCitation[];
  followupQuestions?: TurnFollowup[];
  onInputQuestion?: (question: string) => void;
}

const agentClassMap: Partial<Record<TurnAgentStyle, string>> = {
  legislation: styles.legislation
};

export const Answer = ({
  content,
  isStreaming,
  agentStyle,
  feedbackSlot,
  onCitationClicked,
  citations = [],
  otherLinks = [],
  followupQuestions = [],
  onInputQuestion
}: Props) => {
  const agentClass = agentStyle ? (agentClassMap[agentStyle] ?? "") : "";
  const answerTextRef = useRef<HTMLDivElement>(null);

  // BE sends HTML directly
  const sanitizedContent = DOMPurify.sanitize(content, {
    ADD_ATTR: ["data-citation-index", "data-citation-text"]
  });

  useEffect(() => {
    const container = answerTextRef.current;
    if (!container) return;

    const handleClick = (event: Event) => {
      event.preventDefault();
      const target = event.currentTarget as HTMLElement;
      const citationIndexAttr = target.getAttribute("data-citation-index");
      const citationText = target.getAttribute("data-citation-text");

      if (citationIndexAttr) {
        const citationIndex = parseInt(citationIndexAttr, 10);
        const citation = citations.find(c => c.index === citationIndex);
        if (citation) {
          onCitationClicked(citation.url, citationText || undefined);
        }
      }
    };

    const supContainers = container.querySelectorAll(".supContainer");
    supContainers.forEach(el => el.addEventListener("click", handleClick));
    return () => {
      supContainers.forEach(el => el.removeEventListener("click", handleClick));
    };
  }, [citations, onCitationClicked, sanitizedContent]);

  return (
    <SectionPage className={`${styles.answerContainer ?? ""} ${agentClass}`.trim()}>
      <Stack>
        <SectionPage className={styles.answerRoleChatGPT}>
          <SnswAvatar />
          <h4 className={styles.answerGPTTitle}>Sage</h4>
        </SectionPage>
      </Stack>

      <Stack.Item>
        <div ref={answerTextRef} className={styles.answerText} dangerouslySetInnerHTML={{ __html: sanitizedContent }}></div>
      </Stack.Item>

      <Citations citations={citations} isStreaming={isStreaming} onCitationClicked={onCitationClicked} />
      <OtherLinks otherLinks={otherLinks} onCitationClicked={onCitationClicked} />
      {!isStreaming && followupQuestions.length > 0 && (
        <div className={styles.relatedSection}>
          <FollowupQuestions questions={followupQuestions} onQuestionClick={q => onInputQuestion?.(q.question)} />
        </div>
      )}
      {!isStreaming && feedbackSlot}
    </SectionPage>
  );
};
